// services/securityService.js
// Simple encryption/decryption without external dependencies

import { getAuthItem } from './authSessionStore';

const SECRET_KEY = process.env.REACT_APP_SECRET_KEY;
if (!SECRET_KEY || String(SECRET_KEY).trim() === '') {
  throw new Error(
    'REACT_APP_SECRET_KEY is not set. Set it in .env (e.g. REACT_APP_SECRET_KEY=your-secret-key) and rebuild the app.'
  );
}

// Simple Base64 + XOR encryption (Level 1 security)
const simpleEncrypt = (text, key) => {
  let encrypted = '';
  for (let i = 0; i < text.length; i++) {
    encrypted += String.fromCharCode(text.charCodeAt(i) ^ key.charCodeAt(i % key.length));
  }
  return btoa(encrypted); // Base64 encode
};

const simpleDecrypt = (encryptedText, key) => {
  try {
    const decoded = atob(encryptedText); // Base64 decode
    let decrypted = '';
    for (let i = 0; i < decoded.length; i++) {
      decrypted += String.fromCharCode(decoded.charCodeAt(i) ^ key.charCodeAt(i % key.length));
    }
    return decrypted;
  } catch (error) {
    return null;
  }
};

// Generate session token
export const generateSessionToken = (userData) => {
  const timestamp = Date.now();
  const sessionData = {
    staffId: userData.StaffId,
    email: userData.EmailId,
    timestamp: timestamp,
    fingerprint: getBrowserFingerprint(),
    version: '1.0'
  };
  
  return simpleEncrypt(JSON.stringify(sessionData), SECRET_KEY);
};

// Validate session token
export const validateSessionToken = (token) => {
  try {
    const decrypted = simpleDecrypt(token, SECRET_KEY);
    if (!decrypted) return { isValid: false, reason: 'Invalid token format' };
    
    const sessionData = JSON.parse(decrypted);
    
    // Check if token is too old (24 hours)
    const maxAge = 24 * 60 * 60 * 1000;
    if (Date.now() - sessionData.timestamp > maxAge) {
      return { isValid: false, reason: 'Token expired' };
    }
    
    // Check browser fingerprint
    if (sessionData.fingerprint !== getBrowserFingerprint()) {
      return { isValid: false, reason: 'Invalid session - different browser' };
    }
    
    return { isValid: true, data: sessionData };
  } catch (error) {
    return { isValid: false, reason: 'Token validation failed' };
  }
};

// Generate browser fingerprint (avoid screen size — changes with DevTools device emulation)
const getBrowserFingerprint = () => {
  const components = [
    navigator.userAgent,
    navigator.language,
    new Date().getTimezoneOffset().toString(),
    navigator.platform
  ];
  
  // Simple hash function
  let hash = 0;
  const str = components.join('|');
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return hash.toString();
};

// Encrypt data
export const encryptData = (data) => {
  return simpleEncrypt(JSON.stringify(data), SECRET_KEY);
};

// Decrypt data
export const decryptData = (encryptedData) => {
  try {
    const decrypted = simpleDecrypt(encryptedData, SECRET_KEY);
    return decrypted ? JSON.parse(decrypted) : null;
  } catch (error) {
    return null;
  }
};

// Validate data integrity
export const validateDataIntegrity = () => {
  try {
    const token = getAuthItem('sessionToken');
    const lastActivity = getAuthItem('lastActivity');
    const isLoggedIn = getAuthItem('isLoggedIn');
    
    // Basic checks
    if (isLoggedIn === 'true' && !token) {
      console.warn('Security: Login status without session token detected');
      return false;
    }
    
    if (token && !validateSessionToken(token).isValid) {
      console.warn('Security: Invalid session token detected');
      return false;
    }
    
    // Check timestamp validity
    if (lastActivity) {
      const activity = parseInt(lastActivity);
      const now = Date.now();
      if (activity > now || activity < (now - 24 * 60 * 60 * 1000)) {
        console.warn('Security: Suspicious activity timestamp detected');
        return false;
      }
    }
    
    return true;
  } catch (error) {
    console.error('Security: Data integrity check failed', error);
    return false;
  }
};

// Security event logging
export const logSecurityEvent = (event, details) => {
  const securityLog = {
    timestamp: new Date().toISOString(),
    event: event,
    details: details,
    fingerprint: getBrowserFingerprint(),
    url: window.location.href
  };
  
  
  // Store recent security events
  const events = JSON.parse(localStorage.getItem('securityEvents') || '[]');
  events.push(securityLog);
  
  // Keep only last 10 events
  if (events.length > 10) {
    events.splice(0, events.length - 10);
  }
  
  localStorage.setItem('securityEvents', JSON.stringify(events));
};

const securityService = {
  generateSessionToken,
  validateSessionToken,
  encryptData,
  decryptData,
  validateDataIntegrity,
  logSecurityEvent
};

export default securityService;